import { OneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { Score, ScoreValue } from "src/api/client-api/post/scoreSchema";
import { ScoreService, ScoreVariation } from "../EntityBasedScore/ScoreService";
import { ActionPlanScoreTargets, SsqPost } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { PostElement } from "src/api/client-api/post/postSchema";
import { Campaign } from "src/api/client-api/useCampaign";

type ScoreLabel = string;
export type APScore = {
    id: number;
    campaign?: Campaign | undefined;
    scoreByLabel: ScoreLabel;
    score: ScoreValue;
    score2: Score;
    elements: PostElement[];
};
type ScoreID = number;

type Goal = {
    value: number | undefined;
    completionPercentage: number;
    goalReductionPercentage: number;
};
type AggPosts = {
    lastItem: APScore | null;
    details: APScore[];
};
export type APMetadata = {
    actionPlan: OneActionPlan;

    goal: Goal | null;
    startingPost: APScore;

    scoresWithActionPost: AggPosts;
    scoresWithoutActionPost: AggPosts;
};

export type ActionPlanData = {
    scoreId: ScoreID;
    variationId: ScoreID | null;
    // unit: string;
    // variations: [],

    metadata: APMetadata;
};

type Options = {
    actionPlan: OneActionPlan | undefined;
    scores: ScoreVariation[];
};
export const useActionPlanData = (options: Options): ActionPlanData[] => {
    const { actionPlan, scores } = options;
    if (actionPlan === undefined) {
        return [];
    }

    const actionPlanByScores = scores.map((score) => {
        const scoreId = score.scoreId;
        const variationId = score.variationId;

        const startingPost = getAPScoreFromPost(actionPlan.startSsqPost, scoreId, score.precision ?? 0);

        const _postsWithoutAP = actionPlan.actionPlanSsqPosts.map((p) => p.ssqPost);
        const postsWithoutAP = translatePostToAggPosts(_postsWithoutAP, scoreId, score.precision);

        const postsWithAP = translatePostToAggPosts(actionPlan.simulationSsqPosts, scoreId, score.precision);

        const scoreTarget = actionPlan.actionPlanScoreTargets.find((scoreTarget) => {
            const targetVarID = scoreTarget.variationId ?? null;
            return scoreTarget.scoreId === scoreId && targetVarID === variationId;
        });

        const goal = computeGoal({
            scoreTarget,
            startingPost,
            postWithoutAP: postsWithoutAP.lastItem,
        });

        const actionPlanMetadata: APMetadata = {
            actionPlan,
            goal,
            startingPost,
            scoresWithActionPost: postsWithAP,
            scoresWithoutActionPost: postsWithoutAP,
        };

        const actionPlanData: ActionPlanData = {
            scoreId,
            variationId,
            metadata: actionPlanMetadata,
        };
        return actionPlanData;
    });

    return actionPlanByScores;
};

type ScoreTarget = {
    value: number;
    percent: number;
};
const getFilledScoreTarget = (
    startingValue: number,
    targetValue: number,
    scoreTarget: ActionPlanScoreTargets,
): ScoreTarget | null => {
    const absolute = scoreTarget.simulationVaryAbsolute;
    const percent = scoreTarget.simulationVaryPercentage;

    if (absolute === undefined && percent === undefined) {
        return null;
    }

    if (absolute === undefined) {
        const nbPercent = percent as number;
        const growth = (startingValue / 100) * nbPercent;

        const valueFromPercent = targetValue - growth;
        return {
            percent: nbPercent,
            value: valueFromPercent,
        };
    }
    const nbValue = absolute as number;

    const percentFromValue = startingValue > 0 ? 100 - (nbValue / startingValue) * 100 : 0;

    return {
        percent: percentFromValue,
        value: nbValue,
    };
};

export type ComputeGoalOptions = {
    startingPost: APScore;
    postWithoutAP: APScore | null;
    scoreTarget: ActionPlanScoreTargets | undefined;
};
const computeGoal = (options: ComputeGoalOptions): Goal | null => {
    const { startingPost, postWithoutAP, scoreTarget } = options;

    if (scoreTarget === undefined) return null;
    if (postWithoutAP === null) return null;

    const startingValue = startingPost.score as number;
    const targetValue = postWithoutAP.score as number;
    const filledScoreTarget = getFilledScoreTarget(startingValue, targetValue, scoreTarget);
    if (filledScoreTarget === null) return null;

    const totalDiff = ScoreService.substractScore(startingPost.score, filledScoreTarget.value ?? null);
    if (totalDiff === null) return null;

    const goalReductionInPercent = startingValue > 0 ? 100 - (filledScoreTarget.value / startingValue) * 100 : 0;

    const completionPercentage = totalDiff > 0 ? 100 - (1000 / totalDiff) * 100 : 0;

    const goal: Goal = {
        value: filledScoreTarget.value,
        completionPercentage: completionPercentage,
        goalReductionPercentage: goalReductionInPercent,
    };
    return goal;
};

// Are those below ONLY Action based ?!
// I wonder
// Since, it takes `[main]` dimension...
type PostWithCampaignAndScores = SsqPost;
const getAPScoreFromPost = (post: PostWithCampaignAndScores, scoreId: number, precision: number): APScore => {
    const postScore = post.scores?.[scoreId]?.score ?? null;
    const postCampaignLabel = post.campaign?.label ?? "";

    const dd = post.scores[scoreId];
    const actionPlanScore: APScore = {
        id: post.id,
        campaign: post.campaign,
        score: ScoreService.normalizeScore(postScore, precision).value,
        scoreByLabel: postCampaignLabel,
        score2: dd,
        elements: post.elements ?? [],
    };
    return actionPlanScore;
};

const translatePostToAggPosts = (
    posts: PostWithCampaignAndScores[],
    scoreId: number,
    _precision?: number,
): AggPosts => {
    const precision = _precision ?? 0;

    const postsDetails = posts.map((post) => {
        const actionPlanScore = getAPScoreFromPost(post, scoreId, precision);
        return actionPlanScore;
    });

    if (postsDetails.length === 0) {
        return {
            lastItem: null,
            details: [],
        };
    }

    const lastItemIndex = postsDetails.length - 1;
    return {
        lastItem: postsDetails[lastItemIndex],
        details: postsDetails,
    };
};
